import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import client from "../api/client";
import { useWebsite } from "../context/WebsiteContext";
import AiFieldActions from "../components/AiFieldActions";
import SimpleTextEditor from "../components/SimpleTextEditor";

const initialForm = {
  title: "",
  severity: "medium",
  status: "open",
  issue_summary: "",
  root_cause: "",
  business_impact: "",
  resolution_notes: "",
  occurred_at: "",
  resolved_at: ""
};

function toMysql(value) {
  if (!value) return null;
  return value.replace("T", " ") + ":00";
}

function getDowntimeMinutes(occurredAt, resolvedAt) {
  if (!occurredAt || !resolvedAt) return null;
  const start = new Date(occurredAt.replace(" ", "T"));
  const end = new Date(resolvedAt.replace(" ", "T"));
  const diff = Math.round((end.getTime() - start.getTime()) / 60000);
  return diff > 0 ? diff : null;
}

function formatDowntime(minutes) {
  if (minutes === null || minutes === undefined) return "in progress";
  const hrs = Math.floor(minutes / 60);
  const min = minutes % 60;
  return `${hrs}h ${min}m`;
}

export default function IncidentsPage({ websiteId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeWebsite } = useWebsite();
  const [tab, setTab] = useState("capture");
  const [incidents, setIncidents] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [aiBusyKey, setAiBusyKey] = useState("");
  const [aiAssistMessage, setAiAssistMessage] = useState("");

  const load = async () => {
    if (!websiteId) return;
    const { data } = await client.get(`/websites/${websiteId}/incidents`);
    setIncidents(data.data || []);
  };

  useEffect(() => {
    load();
  }, [websiteId]);

  useEffect(() => {
    const onTabChangeRequest = (event) => {
      const next = event?.detail?.tab;
      if (next === "capture" || next === "preview") {
        setTab(next);
      }
    };
    window.addEventListener("wpa:incidents-tab-change-request", onTabChangeRequest);
    return () => window.removeEventListener("wpa:incidents-tab-change-request", onTabChangeRequest);
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("wpa:incidents-active-tab", { detail: { tab } }));
  }, [tab]);

  const downtimePreview = useMemo(() => {
    return getDowntimeMinutes(toMysql(form.occurred_at || ""), toMysql(form.resolved_at || ""));
  }, [form.occurred_at, form.resolved_at]);

  const create = async (event) => {
    event.preventDefault();
    if (!websiteId) return;

    const occurred = toMysql(form.occurred_at) || new Date().toISOString().slice(0, 19).replace("T", " ");
    const resolved = toMysql(form.resolved_at);
    const downtime = getDowntimeMinutes(occurred, resolved || "");

    setSaving(true);
    try {
      await client.post(`/websites/${websiteId}/incidents`, {
        title: form.title,
        severity: form.severity,
        status: form.status,
        details: form.issue_summary,
        issue_summary: form.issue_summary,
        root_cause: form.root_cause,
        business_impact: form.business_impact,
        resolution_notes: form.resolution_notes,
        occurred_at: occurred,
        resolved_at: resolved,
        downtime_minutes: downtime
      });
      setForm(initialForm);
      setTab("preview");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const runAiAssist = async (field, mode, fieldLabel) => {
    if (!websiteId) return;
    setAiBusyKey(`${field}:${mode}`);
    setAiAssistMessage("");
    try {
      const { data } = await client.post("/ai/text-assist", {
        website_id: websiteId,
        mode,
        field_label: fieldLabel,
        current_text: form[field] || "",
        context: {
          page: "incidents",
          website_name: activeWebsite?.name || "",
          incident_title: form.title || "",
          severity: form.severity,
          status: form.status,
          issue_summary: form.issue_summary,
          root_cause: form.root_cause,
          business_impact: form.business_impact,
          resolution_notes: form.resolution_notes
        }
      }, {
        skipAuthRedirect: true
      });
      setForm((prev) => ({ ...prev, [field]: data?.data?.text || "" }));
    } catch (error) {
      setAiAssistMessage(error?.response?.data?.message || "AI assist could not generate content for this incident field.");
    } finally {
      setAiBusyKey("");
    }
  };

  const generateIncidentFormWithAi = async () => {
    if (!websiteId) return;
    setAiBusyKey("form:generate");
    setAiAssistMessage("");
    try {
      const nextForm = { ...form };
      const fields = [
        ["issue_summary", "Incident Issue Summary"],
        ["root_cause", "Incident Root Cause"],
        ["business_impact", "Incident Business Impact"],
        ["resolution_notes", "Incident Resolution Notes"]
      ];

      for (const [field, fieldLabel] of fields) {
        const { data } = await client.post("/ai/text-assist", {
          website_id: websiteId,
          mode: "generate",
          field_label: fieldLabel,
          current_text: nextForm[field] || "",
          context: {
            page: "incidents",
            website_name: activeWebsite?.name || "",
            incident_title: nextForm.title || "",
            severity: nextForm.severity,
            status: nextForm.status,
            issue_summary: nextForm.issue_summary,
            root_cause: nextForm.root_cause,
            business_impact: nextForm.business_impact,
            resolution_notes: nextForm.resolution_notes
          }
        }, {
          skipAuthRedirect: true
        });
        const generated = data?.data?.text || "";
        if (generated) {
          nextForm[field] = generated;
        }
      }

      setForm(nextForm);
    } catch (error) {
      setAiAssistMessage(error?.response?.data?.message || "AI form generation failed for incidents.");
    } finally {
      setAiBusyKey("");
    }
  };

  if (!websiteId) {
    return <main className="page-grid"><section className="card-modern"><p>Select a website from the top switcher to manage incidents.</p></section></main>;
  }

  useEffect(() => {
    const onGenerateForm = () => {
      if (!aiBusyKey) {
        generateIncidentFormWithAi();
      }
    };
    window.addEventListener("wpa:incidents-generate-form", onGenerateForm);
    return () => window.removeEventListener("wpa:incidents-generate-form", onGenerateForm);
  }, [aiBusyKey, websiteId, form]);

  useEffect(() => {
    const pending = location.state?.pendingWorkspaceAction;
    if (pending === "wpa:incidents-generate-form") {
      generateIncidentFormWithAi();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  return (
    <main className="page-grid">
      {tab === "capture" ? (
        <section className="card-modern">
          <h3>Create Incident</h3>
          <p className="muted">Website: <strong>{activeWebsite?.name || "Selected website"}</strong></p>
          {aiAssistMessage ? <p className="error">{aiAssistMessage}</p> : null}
          <form onSubmit={create}>
            <input required placeholder="Incident title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <div className="inline-grid two">
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="critical">critical</option>
              </select>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="open">open</option>
                <option value="investigating">investigating</option>
                <option value="resolved">resolved</option>
              </select>
            </div>
            <label>
              <span>Issue observed at</span>
              <input type="datetime-local" value={form.occurred_at} onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} />
            </label>
            <label>
              <span>Resolved at (optional)</span>
              <input type="datetime-local" value={form.resolved_at} onChange={(e) => setForm({ ...form, resolved_at: e.target.value })} />
            </label>
            <div>
              <SimpleTextEditor required rows={5} placeholder="What happened?" value={form.issue_summary} onChange={(value) => setForm({ ...form, issue_summary: value })} ariaLabel="incident-issue-summary" />
              <AiFieldActions
                helper="Draft the incident description"
                loadingMode={aiBusyKey === "issue_summary:generate" ? "generate" : aiBusyKey === "issue_summary:rewrite" ? "rewrite" : ""}
                onGenerate={() => runAiAssist("issue_summary", "generate", "Incident Issue Summary")}
                onRewrite={() => runAiAssist("issue_summary", "rewrite", "Incident Issue Summary")}
              />
            </div>
            <div>
              <SimpleTextEditor rows={5} placeholder="Likely root cause" value={form.root_cause} onChange={(value) => setForm({ ...form, root_cause: value })} ariaLabel="incident-root-cause" />
              <AiFieldActions
                helper="Draft or refine the root cause"
                loadingMode={aiBusyKey === "root_cause:generate" ? "generate" : aiBusyKey === "root_cause:rewrite" ? "rewrite" : ""}
                onGenerate={() => runAiAssist("root_cause", "generate", "Incident Root Cause")}
                onRewrite={() => runAiAssist("root_cause", "rewrite", "Incident Root Cause")}
              />
            </div>
            <div>
              <SimpleTextEditor rows={5} placeholder="Business impact" value={form.business_impact} onChange={(value) => setForm({ ...form, business_impact: value })} ariaLabel="incident-business-impact" />
              <AiFieldActions
                helper="Describe business impact professionally"
                loadingMode={aiBusyKey === "business_impact:generate" ? "generate" : aiBusyKey === "business_impact:rewrite" ? "rewrite" : ""}
                onGenerate={() => runAiAssist("business_impact", "generate", "Incident Business Impact")}
                onRewrite={() => runAiAssist("business_impact", "rewrite", "Incident Business Impact")}
              />
            </div>
            <div>
              <SimpleTextEditor rows={5} placeholder="Resolution notes" value={form.resolution_notes} onChange={(value) => setForm({ ...form, resolution_notes: value })} ariaLabel="incident-resolution-notes" />
              <AiFieldActions
                helper="Draft clear resolution notes"
                loadingMode={aiBusyKey === "resolution_notes:generate" ? "generate" : aiBusyKey === "resolution_notes:rewrite" ? "rewrite" : ""}
                onGenerate={() => runAiAssist("resolution_notes", "generate", "Incident Resolution Notes")}
                onRewrite={() => runAiAssist("resolution_notes", "rewrite", "Incident Resolution Notes")}
              />
            </div>
            <p className="muted">Downtime preview: <strong>{formatDowntime(downtimePreview)}</strong></p>
            <button type="submit" disabled={saving}>{saving ? "Saving..." : "Save Incident"}</button>
          </form>
        </section>
      ) : (
        <section className="card-modern">
          <h3>Incident Preview</h3>
          <div className="incident-list">
            {incidents.map((incident) => (
              <article key={incident.id} className="incident-item">
                <div className="incident-head">
                  <strong>{incident.title}</strong>
                  <span className={`status-chip status-${incident.status}`}>{incident.status}</span>
                </div>
                <p><strong>Severity:</strong> {incident.severity}</p>
                <p><strong>Occurred:</strong> {incident.occurred_at}</p>
                <p><strong>Resolved:</strong> {incident.resolved_at || "Not yet"}</p>
                <p><strong>Downtime:</strong> {formatDowntime(incident.downtime_minutes)}</p>
                <p><strong>Issue:</strong> {incident.issue_summary || incident.details || "-"}</p>
                <p><strong>Root Cause:</strong> {incident.root_cause || "-"}</p>
                <p><strong>Business Impact:</strong> {incident.business_impact || "-"}</p>
                <p><strong>Resolution Notes:</strong> {incident.resolution_notes || "-"}</p>
              </article>
            ))}
            {!incidents.length ? <p className="muted">No incidents added for this website yet.</p> : null}
          </div>
        </section>
      )}
    </main>
  );
}
